import type { SearchResultItem } from '@/api/generated/model';
import { formatSearchResultMetadata } from '@/features/search/searchMetadata';
import { globalSearch } from '../generated/search/search';

export interface SearchOptions {
  query: string;
  types?: Array<'accounts' | 'transactions' | 'goals' | 'documents' | 'parties'>;
  limit?: number;
  offset?: number;
}

export interface SearchResult extends SearchResultItem {
  href?: string;
}

export interface EnhancedSearchResponse {
  query: string;
  totalResults: number;
  results: {
    accounts: SearchResult[];
    transactions: SearchResult[];
    goals: SearchResult[];
    documents: SearchResult[];
    parties: SearchResult[];
  };
  hasMore: boolean;
}

let debounceTimeoutId: ReturnType<typeof setTimeout> | undefined;

function mapResults(items: SearchResultItem[] | undefined): SearchResult[] {
  return items?.map((item) => ({ ...item })) ?? [];
}

export const searchService = {
  async search(options: SearchOptions): Promise<EnhancedSearchResponse> {
    const response = await globalSearch({
      query: options.query,
      types: options.types,
      limit: options.limit || 20,
      offset: options.offset || 0,
    });

    return {
      query: response.query,
      totalResults: response.totalResults,
      hasMore: response.hasMore,
      results: {
        accounts: mapResults(response.results.accounts),
        transactions: mapResults(response.results.transactions),
        goals: mapResults(response.results.goals),
        documents: mapResults(response.results.documents),
        parties: mapResults(response.results.parties),
      },
    };
  },

  async searchWithDebounce(
    options: SearchOptions,
    debounceMs = 300
  ): Promise<EnhancedSearchResponse> {
    return new Promise((resolve, reject) => {
      debounceTimeoutId = setTimeout(async () => {
        try {
          resolve(await this.search(options));
        } catch (error) {
          reject(error);
        }
      }, debounceMs);
    });
  },

  cancelPendingSearch() {
    if (debounceTimeoutId) {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = undefined;
    }
  },

  getSearchSuggestions(query: string): string[] {
    const suggestions: string[] = [];
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('transaction')) {
      suggestions.push('transactions this month', 'transaction history', 'recent transactions');
    }
    if (lowerQuery.includes('account')) {
      suggestions.push('account balance', 'account statements', 'savings account');
    }
    if (lowerQuery.includes('goal')) {
      suggestions.push('savings goals', 'financial goals', 'goal progress');
    }
    if (lowerQuery.includes('document')) {
      suggestions.push('tax documents', 'bank statements', 'investment reports');
    }

    return suggestions.slice(0, 5);
  },

  formatSearchResult(result: SearchResult): {
    title: string;
    description: string;
    subtitle?: string;
  } {
    const descriptions: Record<string, string> = {
      account: 'Account',
      transaction: 'Transaction',
      goal: 'Financial Goal',
      document: 'Document',
      party: 'Contact',
    };

    return {
      title: result.title,
      description: result.description || descriptions[result.type] || '',
      subtitle: formatSearchResultMetadata(result),
    };
  },
};
